import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  Briefcase,
  CalendarCheck,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  FileCheck,
  FileText,
  GitMerge,
  Layers,
  Loader2,
  Mail,
  Phone,
  Send,
  ShieldAlert,
  Sparkles,
  Trash2,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";
import { useCandidate, useNeeds, useCandidateMatches } from "@/hooks/useStaffing";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageField from "@/components/pilotage/PilotageField";
import CvPreviewModal from "@/components/admin/recruitment/CvPreviewModal";
import { Input } from "@/components/ui/input";
import {
  COLLABORATOR_STATUSES,
  CONTRACT_KINDS,
  createCollaborator as pilotageCreateCollaborator,
} from "@/lib/pilotageApi";
import {
  createApplication,
  deleteCandidate,
  generateMergeToken,
  mergeCandidates,
  updateCandidatePoolStatus,
} from "@/lib/recruitmentApi";
import toast from "react-hot-toast";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import StartRecruitmentModal from "@/components/admin/recruitment/StartRecruitmentModal";
import CandidateSavedProfile from "@/components/admin/recruitment/CandidateSavedProfile";
import { downloadOriginalCV } from "@/lib/recruitmentApi";
import CvCycleWorkflowPanel from "@/components/admin/recruitment/CvCycleWorkflowPanel";
import InterviewsWorkflowModal from "@/components/admin/recruitment/InterviewsWorkflowModal";
import EmailPreviewModal from "@/components/admin/recruitment/EmailPreviewModal";

const PIPELINE_STAGES = ["applied", "screening", "interview", "offer", "accepted", "hired", "rejected"];

const STAGE_COLORS = {
  applied: "bg-slate-600 text-slate-200",
  screening: "bg-blue-600 text-blue-100",
  interview: "bg-amber-600 text-amber-100",
  offer: "bg-violet-600 text-violet-100",
  accepted: "bg-emerald-600 text-emerald-100",
  hired: "bg-teal-600 text-teal-100",
  rejected: "bg-red-700 text-red-100",
};

const STAGE_LABELS = {
  applied: "Nouvelle",
  screening: "Pré-qualif",
  interview: "Entretien",
  offer: "Offre",
  accepted: "Accepté",
  hired: "Recruté",
  rejected: "Refusé",
};

export default function CandidateDetailPage() {
  const { candidateId } = useParams();
  const {
    candidate,
    loading,
    error,
    fetchCandidate,
    moveStage,
    linkNeed,
    sendCvReview,
    createCollab,
  } = useCandidate(candidateId);
  const { needs } = useNeeds();
  const navigate = useNavigate();

  const [showStartRecruitment,setShowStartRecruitment] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Active sub-tab
  const [activeTab, setActiveTab] = useState("cv_cycles"); // "cv_cycles" | "pipeline" | "matching" | "info"

  // Modals state
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Candidate Delete Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleConfirmDeleteCandidate = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCandidate(candidateId, deleteReason || "Suppression demandée par l'administrateur", true);
      toast.success("Candidat supprimé avec succès.");
      navigate("/admin/recrutement/candidats");
    } catch (err) {
      setDeleteError(err?.response?.data?.detail || "Échec de la suppression du candidat.");
    } finally {
      setDeleting(false);
    }
  };

  // Talent Pool modal state
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [showRemovePoolModal, setShowRemovePoolModal] = useState(false);
  const [removingFromPool, setRemovingFromPool] = useState(false);
  const [poolDelegationForm, setPoolDelegationForm] = useState({
    director_name: "",
    rh_name: "",
    reason: "",
  });
  const [poolSubmitting, setPoolSubmitting] = useState(false);

  // L2 Merge Modal state
  const [selectedDuplicateForMerge, setSelectedDuplicateForMerge] = useState(null);
  const [mergeToken, setMergeToken] = useState(null);
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  // Link Need state
  const [showLinkNeed, setShowLinkNeed] = useState(false);
  const [selectedNeedId, setSelectedNeedId] = useState("");

  // Legacy pilotage modal state
  const [pilotageModalOpen, setPilotageModalOpen] = useState(false);
  const [pilotageCreating, setPilotageCreating] = useState(false);
  const [pilotageError, setPilotageError] = useState("");
  const [pilotageForm, setPilotageForm] = useState({
    full_name: "",
    email: "",
    status: "ACTIVE",
    contract_kind: "FREELANCE",
    internal_cost_monthly: "",
    entry_date: "",
    exit_date: "",
  });

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-sm">
        {error || "Candidat introuvable."}
      </div>
    );
  }

  const pipeline = candidate.pipeline || [];
  const isInPool = candidate.pool_status === "in_pool" || candidate.pool?.status === "in_pool";
  const duplicates = candidate.duplicates_check?.matches || [];

  // L2: Generate Merge Token & open confirmed merge modal
  const handleInitiateMerge = async (dup) => {
    setSelectedDuplicateForMerge(dup);
    setMergeError(null);
    setMergeConfirmed(false);
    try {
      const res = await generateMergeToken(candidateId, dup.candidate_id);
      setMergeToken(res.confirmation_token);
    } catch (err) {
      setMergeError(err?.response?.data?.detail || "Échec d'obtention du token de confirmation de fusion.");
    }
  };

  const handleConfirmMerge = async () => {
    if (!mergeConfirmed || !mergeToken || !selectedDuplicateForMerge) return;
    setMerging(true);
    setMergeError(null);
    try {
      await mergeCandidates(candidateId, {
        source_candidate_id: selectedDuplicateForMerge.candidate_id,
        confirmation_token: mergeToken,
        confirmed: true,
        idempotency_key: `merge_${candidateId}_${Date.now()}`,
      });
      setSelectedDuplicateForMerge(null);
      setMergeToken(null);
      await fetchCandidate();
      alert("Fusion des fiches candidat réalisée avec succès.");
    } catch (err) {
      setMergeError(err?.response?.data?.detail || "Échec de la fusion des candidats.");
    } finally {
      setMerging(false);
    }
  };

  // Talent pool admission with delegation
  const handlePoolSubmit = async (e) => {
    e.preventDefault();
    if (!poolDelegationForm.director_name.trim() || !poolDelegationForm.reason.trim()) {
      alert("Veuillez renseigner le directeur ayant donné son accord et le motif.");
      return;
    }
    setPoolSubmitting(true);
    try {
      const fullReason = `Accord Directeur : ${poolDelegationForm.director_name} | Validé par RH : ${poolDelegationForm.rh_name || "RH"} | Motif : ${poolDelegationForm.reason}`;
      await updateCandidatePoolStatus(candidateId, "in_pool", fullReason);
      setShowPoolModal(false);
      setPoolDelegationForm({ director_name: "", rh_name: "", reason: "" });
      await fetchCandidate();
    } catch (err) {
      alert("Échec d'inscription : " + (err?.response?.data?.detail || err.message));
    } finally {
      setPoolSubmitting(false);
    }
  };

  const handleRemoveFromPool = () => {
    setShowRemovePoolModal(true);
  };

  const confirmRemoveFromPool = async (reason) => {
    setRemovingFromPool(true);
    try {
      await updateCandidatePoolStatus(candidateId, "removed", reason || "Retrait manuel par RH");
      toast.success("Candidat retiré du vivier.");
      setShowRemovePoolModal(false);
      await fetchCandidate();
    } catch (err) {
      toast.error("Échec du retrait : " + (err?.response?.data?.detail || err.message));
    } finally {
      setRemovingFromPool(false);
    }
  };

  const handleLinkNeedSubmit = async () => {
    if (!selectedNeedId) return;
    try {
      const app = await createApplication({candidate_id:candidateId,need_id:selectedNeedId});
      navigate(`/admin/recrutement/pipeline?application_id=${app._id || app.id}`);
      setShowLinkNeed(false);
      setSelectedNeedId("");
    } catch (err) {
      alert("Échec de l'association : " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleOpenPilotageModal = () => {
    setPilotageForm({
      full_name: `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim(),
      email: candidate.email || "",
      status: "ACTIVE",
      contract_kind: "FREELANCE",
      internal_cost_monthly: "",
      entry_date: "",
      exit_date: "",
    });
    setPilotageModalOpen(true);
  };

  const handleCreatePilotageCollab = async (e) => {
    e.preventDefault();
    setPilotageCreating(true);
    setPilotageError("");
    try {
      await pilotageCreateCollaborator({
        ...pilotageForm,
        internal_cost_monthly: pilotageForm.internal_cost_monthly ? parseFloat(pilotageForm.internal_cost_monthly) : null,
        entry_date: pilotageForm.entry_date || null,
        exit_date: pilotageForm.exit_date || null,
        candidate_id: candidateId,
      });
      setPilotageModalOpen(false);
      alert("Collaborateur créé avec succès dans le module Pilotage.");
    } catch (err) {
      setPilotageError(err?.response?.data?.detail || "Erreur de création du collaborateur");
    } finally {
      setPilotageCreating(false);
    }
  };

  return (
    <div className="space-y-6 pb-20" data-testid="candidate-detail-page">
      <div className="flex flex-wrap gap-3">
        <button data-testid="start-recruitment-button" onClick={() => setShowStartRecruitment(true)} className="min-h-11 px-4 rounded-lg bg-blue-600 text-white">Démarrer un recrutement</button>
        {candidate.cv_draft_id && (
          <>
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="min-h-11 px-4 py-2.5 text-blue-300 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/30 rounded-lg inline-flex items-center gap-2 transition-colors text-sm font-medium"
              title="Prévisualiser le CV"
              data-testid="preview-candidate-cv-btn"
            >
              <Eye className="w-4 h-4 text-blue-400" />
              Prévisualiser le CV
            </button>
            <Link className="min-h-11 px-4 py-3 text-blue-300 border border-white/20 rounded-lg inline-flex items-center gap-2 text-sm" to={`/admin/recrutement/cv-builder/${candidate.cv_draft_id}`}>
              Ouvrir le CV
            </Link>
            <button className="min-h-11 px-4 text-blue-300 text-sm hover:text-white transition-colors" onClick={() => downloadOriginalCV(candidate.cv_draft_id).catch(() => toast.error('Document original indisponible'))}>
              Télécharger le CV original
            </button>
          </>
        )}
      </div>
      {showStartRecruitment && <StartRecruitmentModal candidateId={candidateId} onClose={() => setShowStartRecruitment(false)} />}
      {candidate.cv_profile && <CandidateSavedProfile candidate={candidate} onSaved={fetchCandidate} />}
      {/* Breadcrumb Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/admin/recrutement/candidats"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour aux candidats
        </Link>
        <span className="text-slate-600">/</span>
        <span className="text-xs text-blue-400 font-mono truncate">
          {candidate.first_name} {candidate.last_name}
        </span>
      </div>

      {/* L2 DUPLICATE ALERT BANNER */}
      {duplicates.length > 0 && (
        <div
          className="glass rounded-2xl p-5 border border-amber-500/30 bg-amber-500/[0.04] space-y-3"
          data-testid="dedup-alert-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-300 font-bold text-sm">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
              <span>Alerte déduplication : {duplicates.length} doublon(s) potentiel(s) détecté(s)</span>
            </div>
            <span className="text-[11px] font-mono text-amber-200/80 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/20">
              Moteur L2 Strict
            </span>
          </div>

          <div className="space-y-2">
            {duplicates.map((dup) => (
              <div
                key={dup.candidate_id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-slate-900/80 border border-white/5 gap-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white text-xs">
                      {dup.first_name} {dup.last_name}
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                        dup.confidence === "strong"
                          ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                          : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                      }`}
                    >
                      Confiance : {dup.confidence}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Rapprochement : {(dup.match_criteria || []).join(", ")}
                    {dup.email && ` • ${dup.email}`}
                    {dup.phone && ` • ${dup.phone}`}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/recrutement/candidats/${dup.candidate_id}`}
                    className="px-2.5 py-1 text-xs bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg"
                  >
                    Examiner fiche
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleInitiateMerge(dup)}
                    className="px-3 py-1 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white rounded-lg shadow-xs flex items-center gap-1"
                    data-testid={`merge-candidate-btn-${dup.candidate_id}`}
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Vérifier & Fusionner
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Candidate Profile Header Card */}
      <section className="glass rounded-3xl p-6 lg:p-8 border border-white/10 space-y-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-extrabold text-xl shadow-lg shrink-0">
              {(candidate.first_name?.[0] || "C") + (candidate.last_name?.[0] || "")}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
                  {candidate.first_name} {candidate.last_name}
                </h1>
                {isInPool && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/20 flex items-center gap-1">
                    <Award className="h-3.5 w-3.5" />
                    Inscrit au vivier
                  </span>
                )}
                {candidate.status === "merged" && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-500/20 text-slate-400 border border-slate-500/30">
                    Fiche fusionnée
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-300 mt-1">
                {candidate.title || "Titre / Rôle non renseigné"}
              </p>
            </div>
          </div>

          {/* Header Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5">
            {isInPool ? (
              <button
                type="button"
                onClick={handleRemoveFromPool}
                className="px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                data-testid="remove-pool-header-btn"
              >
                <UserMinus className="h-3.5 w-3.5" />
                Retirer du vivier
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowPoolModal(true)}
                className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-md transition-colors"
                data-testid="admit-pool-header-btn"
              >
                <Award className="h-3.5 w-3.5" />
                Inscrire au vivier (RH)
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowInterviewModal(true)}
              className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-md transition-colors"
              data-testid="schedule-interview-header-btn"
            >
              <CalendarCheck className="h-3.5 w-3.5" />
              Planifier entretien
            </button>

            <button
              type="button"
              onClick={() => setShowEmailModal(true)}
              className="px-3 py-1.5 rounded-xl bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors"
              data-testid="refusal-email-header-btn"
            >
              <Mail className="h-3.5 w-3.5" />
              Email refus
            </button>

            <button
              type="button"
              onClick={() => {
                setShowDeleteModal(true);
                setDeleteReason("");
                setDeleteError(null);
              }}
              className="px-3 py-1.5 rounded-xl bg-rose-600/10 hover:bg-rose-600/20 text-rose-300 border border-rose-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors"
              title="Supprimer définitivement ce candidat"
              data-testid="delete-candidate-header-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Supprimer le candidat
            </button>
          </div>
        </div>

        {/* Contact & Meta Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-white/5 text-xs">
          <div>
            <span className="text-slate-500 block">Email</span>
            <span className="font-semibold text-slate-200 truncate block">{candidate.email}</span>
          </div>
          <div>
            <span className="text-slate-500 block">Téléphone</span>
            <span className="font-semibold text-slate-200">{candidate.phone || "Non renseigné"}</span>
          </div>
          <div>
            <span className="text-slate-500 block">Disponibilité</span>
            <span className="font-semibold text-slate-200">{candidate.availability || "Immédiate"}</span>
          </div>
          <div>
            <span className="text-slate-500 block">Source</span>
            <span className="font-semibold text-slate-200">{candidate.source || "Manuel"}</span>
          </div>
        </div>
      </section>

      {/* Navigation Sub-Tabs */}
      <div className="flex border-b border-white/10 gap-6">
        {[
          { id: "cv_cycles", label: "Cycles CV & Versions (L4-L6)", icon: FileText },
          { id: "pipeline", label: `Candidatures & Pipeline (${pipeline.length})`, icon: Briefcase },
          { id: "matching", label: "Besoins correspondants", icon: Sparkles },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-xs font-semibold flex items-center gap-2 border-b-2 transition-all ${
                isActive
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`candidate-tab-${tab.id}`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1: CV Cycles & Document Versions (L4-L6 Integrated) */}
      {activeTab === "cv_cycles" && (
        <div className="space-y-4">
          <CvCycleWorkflowPanel
            candidateId={candidateId}
            onOpenDraft={(draftId) => navigate(`/admin/recrutement/cv-builder/${draftId}`)}
          />
        </div>
      )}

      {/* TAB 2: Applications & Pipeline (L1 Integrated) */}
      {activeTab === "pipeline" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">Missions positionnées</h2>
            <button
              onClick={() => setShowLinkNeed(true)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
            >
              + Positionner sur un besoin
            </button>
          </div>

          {pipeline.length === 0 ? (
            <div className="glass rounded-2xl p-8 border border-white/10 text-center text-slate-500 text-xs">
              Ce candidat n&apos;est positionné sur aucun besoin client.
            </div>
          ) : (
            <div className="space-y-3">
              {pipeline.map((entry) => {
                const need = entry.need || {};

                return (
                  <div
                    key={entry._id}
                    className="glass rounded-2xl p-5 border border-white/10 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <Link
                          to={`/admin/recrutement/pipeline?application_id=${entry._id || entry.id}`}
                          className="font-bold text-white hover:text-blue-300 text-sm"
                        >
                          {need.title || "Parcours vivier"}
                        </Link>
                        <p className="text-xs text-slate-400">
                          Étape : <span className="text-blue-400 font-semibold">{STAGE_LABELS[entry.stage] || entry.stage}</span>
                        </p>
                      </div>

                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                          STAGE_COLORS[entry.stage] || STAGE_COLORS.applied
                        }`}
                      >
                        {STAGE_LABELS[entry.stage] || entry.stage}
                      </span>
                    </div>

                    <Link className="inline-flex min-h-11 items-center text-blue-300 underline" to={`/admin/recrutement/pipeline?application_id=${entry._id || entry.id}`}>Voir le pipeline</Link>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Matching Needs */}
      {activeTab === "matching" && (
        <MatchingNeedsSection
          candidateId={candidateId}
          currentPipeline={pipeline}
          handleLinkNeed={linkNeed}
        />
      )}

      {/* MODALS */}

      {/* L2 Confirmed Merge Modal */}
      {selectedDuplicateForMerge && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-amber-500/30 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-amber-400" />
              Confirmation explicite de fusion (L2)
            </h2>
            <p className="text-xs text-slate-300 leading-relaxed">
              Vous êtes sur le point de fusionner le profil source{" "}
              <span className="font-semibold text-amber-300">
                {selectedDuplicateForMerge.first_name} {selectedDuplicateForMerge.last_name}
              </span>{" "}
              vers la fiche actuelle. Toutes les candidatures, cycles CV et entretiens seront rattachés de manière irréversible.
            </p>

            {mergeError && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                {mergeError}
              </div>
            )}

            <div className="p-3 rounded-xl bg-slate-900 border border-white/10 text-xs space-y-1 font-mono">
              <div>Fiche cible : {candidateId}</div>
              <div>Fiche source à absorber : {selectedDuplicateForMerge.candidate_id}</div>
              <div>Token de fusion actif : {mergeToken ? "✓ Validé" : "En cours..."}</div>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-slate-300 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={mergeConfirmed}
                onChange={(e) => setMergeConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-slate-700 text-amber-600 focus:ring-amber-500"
                data-testid="merge-confirm-checkbox"
              />
              <span>
                Je confirme avoir vérifié l&apos;identité et demande explicitement la fusion irréversible des dossiers.
              </span>
            </label>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSelectedDuplicateForMerge(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmMerge}
                disabled={!mergeConfirmed || !mergeToken || merging}
                className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md flex items-center gap-1.5"
                data-testid="confirm-merge-submit-btn"
              >
                {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Fusionner définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Talent Pool Delegation Modal */}
      {showPoolModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-amber-500/30 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-400" />
              Inscription au Vivier (Accord Direction)
            </h2>
            <p className="text-xs text-slate-400">
              Traçabilité obligatoire de l&apos;accord de direction pour l&apos;admission au vivier.
            </p>

            <form onSubmit={handlePoolSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Directeur ayant donné son accord *</label>
                <input
                  type="text"
                  required
                  placeholder="ex: Marc Dupont (CTO)"
                  value={poolDelegationForm.director_name}
                  onChange={(e) => setPoolDelegationForm({ ...poolDelegationForm, director_name: e.target.value })}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                  data-testid="candidate-pool-director"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Collaborateur RH validant *</label>
                <input
                  type="text"
                  required
                  placeholder="ex: Julie Martin (RH)"
                  value={poolDelegationForm.rh_name}
                  onChange={(e) => setPoolDelegationForm({ ...poolDelegationForm, rh_name: e.target.value })}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                  data-testid="candidate-pool-rh"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Motif / Contexte *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="ex: Profil solide validé en entretien préliminaire..."
                  value={poolDelegationForm.reason}
                  onChange={(e) => setPoolDelegationForm({ ...poolDelegationForm, reason: e.target.value })}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                  data-testid="candidate-pool-reason"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowPoolModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={poolSubmitting}
                  className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-xs font-semibold text-white shadow-md"
                  data-testid="candidate-pool-submit"
                >
                  {poolSubmitting ? "Enregistrement..." : "Confirmer inscription"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link to Need Modal */}
      {showLinkNeed && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-400" />
              Positionner sur un besoin client
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Sélectionner le besoin *</label>
                <select
                  value={selectedNeedId}
                  onChange={(e) => setSelectedNeedId(e.target.value)}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="">-- Choisir une mission --</option>
                  {needs
                    .filter(
                      (n) =>
                        n &&
                        n.status !== "cancelled" &&
                        String(n.status || "").trim().toLowerCase() !== "deleted" &&
                        !n.is_deleted &&
                        !(n.deleted_at != null && String(n.deleted_at).trim() !== "")
                    )
                    .map((n) => (
                    <option key={n._id || n.id} value={n._id || n.id}>
                      {n.title} {n.client_id ? `(${n.client_id})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowLinkNeed(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleLinkNeedSubmit}
                  disabled={!selectedNeedId}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-md"
                >
                  Positionner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interviews Workflow Modal (L7) */}
      <InterviewsWorkflowModal
        isOpen={showInterviewModal}
        onClose={() => {
          setShowInterviewModal(false);
          void fetchCandidate();
        }}
        candidateId={candidateId}
        candidateName={`${candidate.first_name} ${candidate.last_name}`}
        applicationId={pipeline[0]?._id}
        missionTitle={pipeline[0]?.need?.title || "Recrutement LENVIA"}
      />

      {/* Email Preview Modal (L7 Refusal) */}
      <EmailPreviewModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          void fetchCandidate();
        }}
        type="refusal"
        context={{
          candidate_id: candidateId,
          candidate_name: `${candidate.first_name} ${candidate.last_name}`,
          candidate_email: candidate.email,
          mission_title: pipeline[0]?.need?.title || "Recrutement LENVIA",
        }}
      />

      {/* Confirmation Modal for Removing from Talent Pool */}
      <ConfirmationModal
        isOpen={showRemovePoolModal}
        onClose={() => setShowRemovePoolModal(false)}
        title="Retirer le candidat du vivier"
        message={`Confirmez-vous le retrait de ${candidate?.first_name} ${candidate?.last_name} du vivier de talents ?`}
        confirmText="Confirmer le retrait"
        variant="warning"
        requireReason={true}
        reasonLabel="Motif du retrait du vivier"
        reasonPlaceholder="ex: Recruté sur mission, indisponible..."
        isSubmitting={removingFromPool}
        onConfirm={confirmRemoveFromPool}
        testId="candidate-detail-remove-pool-modal"
      />

      {candidate.cv_draft_id && isPreviewOpen && (
        <CvPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          draftId={candidate.cv_draft_id}
          candidateName={`${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() || "Candidat"}
          jobTitle={candidate.title || ""}
        />
      )}

      {/* Delete Candidate Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-rose-500/40 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Supprimer le candidat</h2>
                <p className="text-xs text-rose-300 font-semibold">
                  {candidate.first_name} {candidate.last_name}
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900 border border-white/10 text-xs space-y-2 text-slate-300">
              <p className="font-semibold text-white">Conséquences de la suppression :</p>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li>Retrait définitif des listes de candidats et du vivier de talents</li>
                <li>Exclusion systématique de tous les futurs algorithmes de matching</li>
                <li>Suppression logique sécurisée (soft delete pour traçabilité et conformité RGPD)</li>
                <li>L&apos;historique passé des décisions RH/client reste archivé</li>
              </ul>
            </div>

            {(candidate.collab_created || candidate.collaborator_id) && (
              <div className="p-3 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 text-xs flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-rose-400" />
                <span>
                  Ce candidat a déjà été converti en collaborateur LENVIA ou dispose d&apos;une mission active. La suppression de sa fiche est interdite.
                </span>
              </div>
            )}

            {pipeline.length > 0 && !(candidate.collab_created || candidate.collaborator_id) && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                <div>
                  <span className="font-semibold">
                    Attention : {pipeline.length} candidature(s) associée(s) à ce profil.
                  </span>
                  <p className="mt-1 text-[11px] text-amber-200/80">
                    La suppression détachera le candidat de ses besoins clients en cours.
                  </p>
                </div>
              </div>
            )}

            {deleteError && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                {deleteError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 block">
                Motif de suppression (optionnel) :
              </label>
              <input
                type="text"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="ex: Demande RGPD du candidat, profil obsolète..."
                className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-rose-500"
                data-testid="delete-candidate-reason-input"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteCandidate}
                disabled={deleting || Boolean(candidate.collab_created || candidate.collaborator_id)}
                className="px-5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md flex items-center gap-1.5"
                data-testid="confirm-delete-candidate-btn"
              >
                {deleting ? "Suppression en cours..." : "Supprimer définitivement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchingNeedsSection({ candidateId, currentPipeline, handleLinkNeed }) {
  const { matches, loading, error } = useCandidateMatches(candidateId);
  const [linkingNeedId, setLinkingNeedId] = useState(null);

  if (loading) {
    return (
      <div className="glass rounded-2xl p-8 border border-white/10 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return null;
  }

  const pipelineNeedIds = new Set(currentPipeline.map((p) => p.need_id));
  const availableMatches = (matches || []).filter((m) => {
    if (pipelineNeedIds.has(m.need_id)) return false;
    if (!m.need) return false;
    if (
      m.need.is_deleted === true ||
      String(m.need.status || "").trim().toLowerCase() === "deleted" ||
      (m.need.deleted_at != null && String(m.need.deleted_at).trim() !== "")
    ) {
      return false;
    }
    return true;
  });

  const renderNeedScoreBadge = (match) => {
    const status = match.score_status;
    const score = match.score;

    if (status === "insufficient_data") {
      return (
        <div
          className="flex flex-col items-center justify-center w-14 h-14 rounded-xl border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-center shrink-0 p-1"
          title="Profil insuffisamment renseigné"
          data-testid={`candidate-match-insufficient-${match.need_id}`}
        >
          <span className="text-[10px] font-bold text-slate-300 leading-tight">Profil</span>
          <span className="text-[8px] uppercase font-bold text-slate-400">Incomplet</span>
        </div>
      );
    }

    if (status === "not_calculated" || score == null) {
      return (
        <div
          className="flex flex-col items-center justify-center w-14 h-14 rounded-xl border border-slate-700 bg-slate-800/50 text-slate-400 font-mono text-center shrink-0 p-1"
          title="Score non calculé"
          data-testid={`candidate-match-uncalculated-${match.need_id}`}
        >
          <span className="text-[10px] font-bold text-slate-400 leading-tight">Non</span>
          <span className="text-[8px] uppercase font-bold text-slate-500">calculé</span>
        </div>
      );
    }

    if (status === "stale") {
      return (
        <div
          className="flex flex-col items-center justify-center w-14 h-14 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono text-center shrink-0 p-1"
          title="Score à actualiser"
          data-testid={`candidate-match-stale-${match.need_id}`}
        >
          <span className="text-lg font-bold">{Math.round(score)}</span>
          <span className="text-[7px] uppercase tracking-wider font-bold text-amber-400">Obsolète</span>
        </div>
      );
    }

    const isTrueZero = score === 0;
    const scoreColor = isTrueZero
      ? "text-slate-400 bg-slate-500/10 border-slate-500/30"
      : score >= 80
      ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
      : score >= 60
      ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
      : "text-slate-400 bg-slate-500/10 border-slate-500/30";

    return (
      <div
        className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl border shrink-0 font-mono ${scoreColor}`}
        title={isTrueZero ? "Vrai zéro : aucun critère ne correspond" : "Score de compatibilité"}
        data-testid={`candidate-match-score-${match.need_id}`}
      >
        <span className="text-xl font-bold">{Math.round(score)}</span>
        <span className="text-[8px] uppercase font-bold">{isTrueZero ? "0 / 100" : "/ 100"}</span>
      </div>
    );
  };

  const onLink = async (needId) => {
    setLinkingNeedId(needId);
    try {
      await handleLinkNeed(needId);
    } finally {
      setLinkingNeedId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="candidate-matching-needs-section">
      <h2 className="text-lg font-bold text-white">Missions correspondantes suggérées (IA)</h2>
      {availableMatches.length === 0 ? (
        <div className="glass rounded-2xl p-8 border border-white/10 text-center text-slate-500 text-xs">
          Le candidat est déjà positionné sur tous les besoins pertinents ou aucun besoin actif ne correspond.
        </div>
      ) : (
        <div className="grid gap-3">
          {availableMatches.map((match) => (
            <div
              key={match.need_id}
              className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
              data-testid={`candidate-match-card-${match.need_id}`}
            >
              <div className="flex items-start gap-4">
                {renderNeedScoreBadge(match)}
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-white text-sm">{match.need?.title}</h3>
                    {(match.need?.company_name || match.need?.client_name) && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/5 border border-white/10 text-slate-300">
                        {match.need?.company_name || match.need?.client_name}
                      </span>
                    )}
                  </div>
                  {match.evaluated_at && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Évalué le {new Date(match.evaluated_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 pt-1.5">
                    {(match.reasons || []).map((r, i) => {
                      const isDem = r.startsWith("Démontré");
                      const isInf = r.startsWith("Inféré");
                      const isMiss = r.startsWith("À vérifier") || r.startsWith("Manque");
                      const badgeColor = isDem
                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                        : isInf
                        ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                        : isMiss
                        ? "bg-rose-500/10 text-rose-300 border-rose-500/30"
                        : "bg-slate-800 text-slate-300 border-slate-700/60";
                      return (
                        <span
                          key={i}
                          className={`text-[11px] px-2 py-0.5 rounded-full border ${badgeColor}`}
                        >
                          {r}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                <Link
                  to={`/admin/recrutement/besoins/${match.need_id}`}
                  className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg"
                >
                  Voir besoin
                </Link>
                <button
                  type="button"
                  onClick={() => onLink(match.need_id)}
                  disabled={linkingNeedId === match.need_id}
                  className="px-4 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-xs disabled:opacity-50"
                  data-testid={`position-from-candidate-${match.need_id}`}
                >
                  {linkingNeedId === match.need_id ? "En cours..." : "Positionner"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
